import { isModelShape } from '@takeshape/schema';
import { List } from 'immutable';
import { createSelector } from 'reselect';
import { getDefaultRuntimeSchema } from '../branches/selectors.js';
import { getSearchState } from '../content-search/selectors.js';
import { getCurrentProjectId } from '../project/selectors.js';
import { getForm } from '../schema-form/selectors.js';
import { SITE_SEARCH_NAME } from '../sites/constants.js';
import { NAME, WEBHOOKS_FORM_NAME } from './constants.js';
import { combineResourceActions, splitResourceActions } from './util.js';

export const getWebhooksState = (state) => state[NAME];

const emptyList = List();

function formatWebhooks(webhook) {
  return webhook
    .update('headers', (headers) => headers || emptyList)
    .merge(splitResourceActions(webhook.get('resourceActions', emptyList)))
    .remove('resourceActions');
}

export const getData = createSelector(getWebhooksState, (state) =>
  state.data.update('webhooks', (webhooks) => webhooks.map(formatWebhooks))
);

const contentTypeOption = (projectId) => (mapping, shape) => {
  const resource = `ts:content-type:${projectId}:default:${shape.id}`;
  mapping[resource] = {
    resource,
    type: 'content-type',
    displayType: 'Shape',
    name: shape.title
  };
  return mapping;
};

const contentOption = (projectId) => (mapping, shape) => {
  const resource = `ts:content:${projectId}:default:${shape.id}:*:*`;
  mapping[resource] = {
    resource,
    type: 'content',
    displayType: 'Data',
    name: shape.title
  };
  return mapping;
};

const siteOption = (projectId) => (mapping, site) => {
  const resource = `ts:site:${projectId}:${site.get('_id')}`;
  mapping[resource] = {
    resource,
    type: 'site',
    displayType: 'Site',
    name: site.get('title')
  };
  return mapping;
};

const searchProps = { searchName: SITE_SEARCH_NAME };
const getSitesResults = (state) => getSearchState(state, searchProps).get('results');

export const getResourceMapping = createSelector(
  [getSitesResults, getDefaultRuntimeSchema, getCurrentProjectId],
  (sitesResults, projectSchema, projectId) => {
    const shapes = Object.values(projectSchema.shapes).filter((shape) => isModelShape(shape) && shape.id !== 'SITE');

    const allSites = `ts:site:${projectId}:*`;
    const allContent = `ts:content:${projectId}:default:*:*:*`;
    const allContentTypes = `ts:content-type:${projectId}:default:*`;

    let mapping = {
      [allSites]: {
        resource: allSites,
        type: 'site',
        displayType: 'Site',
        name: '*'
      },
      [allContent]: {
        resource: allContent,
        type: 'content',
        displayType: 'Data',
        name: '*'
      },
      [allContentTypes]: {
        resource: allContentTypes,
        type: 'content-type',
        displayType: 'Shape',
        name: '*'
      }
    };

    mapping = sitesResults.reduce(siteOption(projectId), mapping);
    mapping = shapes.reduce(contentOption(projectId), mapping);
    mapping = shapes.reduce(contentTypeOption(projectId), mapping);

    return mapping;
  }
);

const webhookFormProps = { formName: WEBHOOKS_FORM_NAME };
const getWebhooksFormData = (state) => getForm(state, webhookFormProps).get('data');

export const getWebhooksUpdate = createSelector([getWebhooksFormData], (formData) => {
  let { secret, webhooks } = formData.toJS();
  webhooks = webhooks.map((webhook) => {
    const { actions, ...rest } = webhook;
    const resources = Object.keys(actions);
    return {
      ...rest,
      resourceActions: combineResourceActions(resources, actions)
    };
  });

  return {
    resetSecret: !secret,
    webhooks
  };
});
